<template>
  <v-card class="card-wrap" :height="height">
    <v-card-title v-if="title">{{ $t('syslog.message.title') }}</v-card-title>
    <v-card-text v-if="instVersion <= 730">
      <div v-if="showDetailMessage" class="card-inner-div" :style="forceHeight">
        <v-row v-for="(msg, idx) in msgs" :key="idx">
          <v-col v-if="msg.tag == 'E'" cols="10" style="height:24px"></v-col>
          <v-col v-if="msg.tag == 'L'" cols="10">
            <p>{{ msg.data }}</p>
          </v-col>
          <v-col v-if="msg.tag == 'T'" cols="10">
            <table class="msg-table">
              <thead>
                <tr>
                  <th v-for="(h, hIdx) in msg.head" :key="hIdx" :class="thbg">{{ h }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td v-for="(d, dIdx) in msg.data" :key="dIdx">{{ d }}</td>
                </tr>
              </tbody>
            </table>
          </v-col>
          <v-col v-if="msg.tag == 'B'" cols="10">
            <table class="msg-table">
              <thead>
                <tr>
                  <th :class="thbg">{{ msg.head }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <p v-for="(d, dIdx) in msg.data" :key="dIdx">{{ d }}</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
    <v-card-text v-if="instVersion > 730">
      <div v-if="showDetailMessage" class="card-inner-div" :style="forceHeight">
        <v-row>
          <v-col cols="4">
            <v-row v-for="(msg, idx) in leftMsgs" :key="idx">
              <v-col cols="11">
                <v-row v-if="msg.tag == 'E'"><p class="label"></p></v-row>
                <fieldset v-if="msg.tag == 'T'">
                  <legend>
                    <span>{{ msg.head }}</span>
                  </legend>
                  <v-row v-for="(datum, dIdx) in msg.data" :key="dIdx">
                    <v-col cols="3">
                      <p class="label">{{ datum[0] }}</p>
                    </v-col>
                    <v-col cols="9"><v-text-field :value="datum[1]" outlined dense hide-details readonly></v-text-field></v-col>
                  </v-row>
                </fieldset>
                <fieldset v-if="msg.tag == 'B'">
                  <legend>
                    <span>{{ msg.head }}</span>
                  </legend>
                  <v-row v-for="(datum, dIdx) in msg.data" :key="dIdx">
                    <v-col cols="12">
                      <p class="label">{{ datum }}</p>
                    </v-col>
                  </v-row>
                </fieldset>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="4">
            <v-row v-for="(msg, idx) in rightMsgs" :key="idx">
              <v-col cols="12">
                <v-row v-if="msg.tag == 'E'"><p class="label"></p></v-row>
                <fieldset v-if="msg.tag == 'T'">
                  <legend>
                    <span>{{ msg.head }}</span>
                  </legend>
                  <v-row v-for="(datum, dIdx) in msg.data" :key="dIdx">
                    <v-col cols="3">
                      <p class="label">{{ datum[0] }}</p>
                    </v-col>
                    <v-col cols="9"><v-text-field :value="datum[1]" outlined dense hide-details readonly></v-text-field></v-col>
                  </v-row>
                </fieldset>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import { api } from '@/api/common/instances';

export default {
  props: {
    systemId: {
      type: String,
      required: true,
    },
    title: {
      type: Boolean,
      default: true,
    },
    height: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      showDetailMessage: false,
      msgs: [],
      leftMsgs: [],
      rightMsgs: [],
    };
  },
  computed: {
    ...mapGetters('common/system', ['getDescendantsById']),
    forceHeight() {
      return `height: ${this.height - 80}px`;
    },
    thbg() {
      return this.$vuetify.theme.isDark ? 'msg-table-dark' : 'msg-table-light';
    },
    instVersion() {
      return this.getCIInstVersion();
    },
  },
  methods: {
    // Grid 내역 재조회가 발생한 경우 부모 컴포넌트가 호출해주면 하면 메시지를 제거해준다.
    removeDetailMessage() {
      this.showDetailMessage = false;
    },
    // Grid 내역이 선택된 경우 부모 컴포넌트가 호출해주면 상세 메시지를 조회한다.
    async getDetailMessage(item) {
      // eslint-disable-next-line prettier/prettier
      const { data } = await api.get('/detailview/dumplog/syslog-detail', {
        params: {
          svrYmdhms: item.svrYmdhms,
          sstmId: item.sstmId,
          fileNo: item.fileNo,
          pos: item.pos,
          instVrsn: this.getCIInstVersion(),
        },
      });
      if (this.getCIInstVersion() <= 730) this.processDetailMessage730(data);
      else this.processDetailMessage(data);
    },
    processDetailMessage730(msgs) {
      /*
        예를 들어, 다음과 같은 포맷으로 내려오는 메시지를 파싱하는 루틴이다.
        [
            "-Details Page for System Log",
            "-",
            "*Date\tTime\tSAP Instance\tType\tNr\tClt\tUser\tTcode\tProgram\tTerminal\tPackage\tGrp\tN\tText",
            "*--\t::\t\t\t\t\t\t\t\t\t\t\t\t",
            "-",
            "-Details",
            "-Recording at local and central time....................... -- ::",
            "*Task......\tProcess\tUser......\tTerminal\tSession\tTCode\tProgram\tCl\tProblem cl\tPackage",
            "*\t\t\t\t\t\t\t\t\t",
            "-",
            "-Further details for this message type",
            "*Module nam\tline\tError text\tCaller....\tReason/cal",
            "*\t\t\t\t",
            "-",
            "=Documentation for system log message   :",
            "=",
            "-",
            "-Technical details",
            "*File\tOffset\tRecFm\tSystem log type\tGrp\tN\tvariable message data",
            "*000000\t0000000000\t\t\t\t\t",
            "-",
            "-EPP Detail information",
            "*Transaction ID\tOveral Cnxt ID\tConnection ID\tCall No.",
            "*\t\t\t"
        ]
        2023.08.07.KWShin.
      */
      this.msgs = [];
      let prevPrefix = '';
      let item = {};
      msgs.reduce((acc, msg) => {
        let prefix = msg.substring(0, 1);
        if (prefix != prevPrefix || (prefix != '*' && prefix != '=')) item = {};
        if (msg == '-') {
          item.tag = 'E';
        } else if (prefix == '-') {
          item.tag = 'L';
          item.data = msg.substring(1, msg.length);
        } else if (prefix == '*') {
          if (prefix != prevPrefix) {
            item.tag = 'T';
            item.head = msg.substring(1, msg.length).split(/\t/g);
          } else {
            item.data = msg.substring(1, msg.length).split(/\t/g);
          }
        } else if (prefix == '=') {
          if (prefix != prevPrefix) {
            item.tag = 'B';
            item.head = msg.substring(1, msg.length);
            item.data = [];
          } else {
            item.data.push(msg.substring(1, msg.length));
          }
        }
        if (prefix != prevPrefix || (prefix != '*' && prefix != '=')) acc.push(item);
        prevPrefix = prefix;
        return acc;
      }, this.msgs);
      this.showDetailMessage = true;
    },
    processDetailMessage(msgs) {
      /*
        예를 들어, 다음과 같은 포맷으로 내려오는 메시지를 파싱하는 루틴이다.
        [
            "-Message Data",
            "*Date / Time\t2023-08-08 04:05:18",
            "*Workprocess Type\tRD",
            "*Number\t000",
            "*Client\t",
            "*User\t",
            "*Transaction Code\t",
            "-",
            "-Message details",
            "*Message ID\tQ0I",
            "*Category\tSAP Basis System",
            "*Message Text\tOperating system call recv failed (error no. 10053)",
            "-",
            "-Documentation",
            "=The specified operating system call was returned with an error.",
            "=For communication calls (receive, send, etc) often the cause of errors",
            "=are network problems.",
            "=",
            "=It could also be a configuration problem at operating system level.",
            "=(file cannot be opened, no space in the file system etc.).",
            "=Additional specifications for error number 10053",
            "=Name for error number : WSAECONNABORTED",
            "=Error message : Software caused connection abort",
            "-",
            "-Session details",
            "*Process ID\t04640",
            "*Session\t0",
            "*Program\t",
            "*Package\tSTSK",
            "*Terminal\t",
            "-",
            "-Technical details",
            "*Syslog type\tm:Error (Function,Module,Row)",
            "*variable data\t          recv10053                   NiIReadrecv   nixxi.cp5087",
            "*EPP Transation ID\t",
            "*EPP Root Context ID\t",
            "*EPP Connection ID\t",
            "*Call Counter\t0",
            "-",
            "-Parameter details",
            "*Message\t          recv10053",
            "*Function\tNiIRead",
            "*Reason\trecv",
            "*Program\tnixxi.cp",
            "*Line\t5087"
        ]
        2023.08.08.KWShin.
      */
      this.msgs = [];
      let prevPrefix = '';
      let item = {};
      msgs.reduce((acc, msg) => {
        if (msg == '-') {
          item = {};
          item.tag = 'E';
          acc.push(item);
          return acc;
        }
        let prefix = msg.substring(0, 1);
        if (prefix == '-') {
          item = {};
          item.head = msg.substring(1, msg.length);
          item.data = [];
          acc.push(item);
        } else if (prefix == '*' && (prevPrefix == '-' || prevPrefix == '*')) {
          item.tag = 'T';
          item.data.push(msg.substring(1, msg.length).split(/\t/g));
        } else if (prefix == '=' && (prevPrefix == '-' || prevPrefix == '=')) {
          item.tag = 'B';
          item.data.push(msg.substring(1, msg.length));
        }
        prevPrefix = prefix;
        return acc;
      }, this.msgs);
      this.leftMsgs = [];
      this.msgs.reduce((acc, elm, idx) => {
        if (idx < 5) acc.push(elm);
        return acc;
      }, this.leftMsgs);
      this.rightMsgs = [];
      this.msgs.reduce((acc, elm, idx) => {
        if (idx > 5) acc.push(elm);
        return acc;
      }, this.rightMsgs);
      this.showDetailMessage = true;
    },
    getCIInstVersion() {
      let ci = this.getDescendantsById(this.systemId).find(inst => inst.centralInstance == true);
      return ci.version;
    },
  },
};
</script>

<style scoped>
.msg-table {
  border-collapse: collapse;
}
.msg-table th {
  padding: 5px 10px 5px 10px;
  border: 1px solid;
}
.msg-table td {
  padding: 10px;
  border: 1px solid;
}
.msg-table-dark {
  background: #2c2c2c;
}
.msg-table-light {
  background: #cacaca;
}
.card-inner-div {
  padding: 20px;
  overflow-y: scroll;
}
fieldset {
  margin-top: 10px;
  padding: 10px;
  border-width: 1px;
}
fieldset legend {
  padding: 0px 10px;
}
.label {
  padding-left: 15px;
}
.v-text-field {
  width: 100%;
}
</style>
